import { ValueChain } from '../../../../../../../common/model/types';
import { ValueChainBreakdown } from '../../../../../../../common/model/output/scores/drilldowns/leena2-top-impacts-types';

export function getValueChainFill(valueChain: ValueChain | undefined) {
  switch (valueChain) {
    case ValueChain.UPSTREAM:
      return '#FF9DCC';
    case ValueChain.INTERNAL:
      return '#240038';
    case ValueChain.DOWNSTREAM:
      return '#F72585';
    case ValueChain.ANY:
      return '#C7C7C7';
    case undefined:
      return 'transparent';
  }
}

/** Human-friendly label for a value chain, matching the `ValueChainsLegend`
 *  wording (Upstream / Internal / Downstream / Any). */
export function valueChainLabel(valueChain: ValueChain): string {
  switch (valueChain) {
    case ValueChain.UPSTREAM:
      return 'Upstream';
    case ValueChain.INTERNAL:
      return 'Internal';
    case ValueChain.DOWNSTREAM:
      return 'Downstream';
    case ValueChain.ANY:
      return 'Any';
  }
}

/** A single non-zero slice of a {@link ValueChainBreakdown}, with its fraction
 *  of the breakdown's own sum. */
export interface ValueChainSegment {
  valueChain: ValueChain;
  /** Magnitude of this value chain in the breakdown. */
  value: number;
  /** `value / Σ(breakdown)` — in `[0, 1]`. */
  fraction: number;
}

/** Stable rendering order for value-chain segments (matches the main impact
 *  chart's UPSTREAM → INTERNAL → DOWNSTREAM order, with ANY last for
 *  legacy/unsplit magnitudes). */
const VALUE_CHAIN_ORDER: ValueChain[] = [
  ValueChain.UPSTREAM,
  ValueChain.INTERNAL,
  ValueChain.DOWNSTREAM,
  ValueChain.ANY,
];

/**
 * Turn a {@link ValueChainBreakdown} into ordered, non-zero segments whose
 * `fraction`s sum to 1 (over the breakdown's own sum). Shared by the impact
 * drilldown tables and the impact-detail dialog so the split logic lives in
 * one place.
 *
 * Returns `[]` (→ caller renders its plain, non-split bar) when the breakdown
 * is absent, empty, sums to ≤ 0, OR carries only an {@link ValueChain.ANY}
 * magnitude — an ANY-only breakdown is the legacy / unsplit case with no
 * genuine value-chain split to show. When ANY co-occurs with real value
 * chains it is kept as a (gray) trailing segment.
 */
export function valueChainSegments(breakdown: ValueChainBreakdown | undefined): ValueChainSegment[] {
  if (!breakdown) {
    return [];
  }
  const total = VALUE_CHAIN_ORDER.reduce((sum, vc) => sum + (breakdown[vc] ?? 0), 0);
  if (total <= 0) {
    return [];
  }
  const segments = VALUE_CHAIN_ORDER.flatMap(valueChain => {
    const value = breakdown[valueChain] ?? 0;
    if (value <= 0) {
      return [];
    }
    return [{ valueChain, value, fraction: value / total }];
  });
  // ANY-only ⇒ no genuine split; let the caller fall back to its plain bar.
  if (segments.length === 1 && segments[0].valueChain === ValueChain.ANY) {
    return [];
  }
  return segments;
}
